/* import __COLOCATED_TEMPLATE__ from './inbox-conversations-table.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import { inject as service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import Component from '@glimmer/component';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type Inbox from 'embercom/objects/inbox/inboxes/inbox';
import { tracked } from 'tracked-built-ins';
import { task, restartableTask } from 'ember-concurrency-decorators';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { type TicketType } from 'embercom/objects/inbox/ticket';
import type Session from 'embercom/services/session';
import { type ColumnDefinition } from 'embercom/objects/inbox/conversations-table-column';
import type ConversationsTableColumn from 'embercom/objects/inbox/conversations-table-column';
import {
  ColumnDefinitionsBuilder,
  ConversationsTableColumns,
  columnId,
} from 'embercom/objects/inbox/conversations-table-column';
import type InboxApi from 'embercom/services/inbox-api';
import type ConversationsTableSettings from 'embercom/services/conversations-table-settings';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import { REQUIRED_CONVERSATIONS_TABLE_FIELDS } from 'embercom/objects/inbox/conversation-table-entry';
import { type SortDirection } from 'embercom/services/inbox-api';
import { action } from '@ember/object';
import {
  type InboxStateOption,
  isConversationStateEqual,
} from 'embercom/models/data/inbox/inbox-filters';
import type InboxState from 'embercom/services/inbox-state';
// @ts-ignore
import { cached, trackedReset } from 'tracked-toolbox';
import { getOwner } from '@ember/application';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import Mentions from 'embercom/objects/inbox/inboxes/mentions';
import type RouterService from '@ember/routing/router-service';
import type PinnedColumnsService from 'embercom/services/pinned-columns-service';
import View from 'embercom/objects/inbox/inboxes/view';
import { type SelectedColumn } from 'embercom/services/conversations-table-settings';
import type IntlService from 'embercom/services/intl';
import { timeout } from 'ember-concurrency';

interface Args {
  inbox: Inbox;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
  Blocks: {
    default: [];
  };
}

export default class InboxConversationsTable extends Component<Signature> {
  @service declare session: Session;
  @service declare inboxApi: InboxApi;
  @service declare conversationsTableSettings: ConversationsTableSettings;
  @service declare inboxState: InboxState;
  @service declare router: RouterService;
  @service declare intercomEventService: any;
  @service declare pinnedColumnsService: PinnedColumnsService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  @trackedReset('settings') selectedStateOption = this.settings.selectedStateOption;
  @trackedReset('settings') selectedMentionsStatus = this.settings.selectedMentionsStatus;
  @trackedReset('settings') selectedSortOption = this.settings.selectedSortOption;
  @tracked searchQuery = '';

  @tracked descriptors: ConversationAttributeDescriptor[] = [];
  @tracked ticketTypes: TicketType[] = [];
  @tracked columnDefinitions?: ColumnDefinition[];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadDynamicColumns).perform();

    registerDestructor(this, () => {
      taskFor(this.loadDynamicColumns).cancelAll();
    });
  }

  get isMentionsInbox() {
    return this.inboxState.activeInbox instanceof Mentions;
  }

  get isViewInbox() {
    return this.args.inbox instanceof View;
  }

  get canManageViewPinnedColumns() {
    return (
      this.session.teammate.permissions.canManageInboxViewsPinnedColumns &&
      this.session.workspace.isFeatureEnabled('inbox-views-pinned-columns') &&
      this.isViewInbox
    );
  }

  @cached
  get settings() {
    return this.conversationsTableSettings.getInboxSettings(this.args.inbox);
  }

  @cached
  get tableColumns() {
    if (!taskFor(this.loadDynamicColumns).lastComplete) {
      return undefined;
    }

    let pinnedColumnIds = this.isViewInbox
      ? (this.args.inbox as View).viewSummary.pinnedColumns || []
      : [];

    return new ConversationsTableColumns(
      getOwner(this),
      this.effectiveSelectedColumns,
      this.columnDefinitions!,
      this.ticketTypes,
      pinnedColumnIds,
    );
  }

  get effectiveSelectedColumns() {
    let baseColumns = this.getBaseSelectedColumns();

    if (this.isSearching) {
      // Add relevantPart column as the first non-fixed column when searching
      let hasRelevantPart = baseColumns.some((col) => col.id === 'relevantPart');
      if (!hasRelevantPart) {
        let fixedColumnsCount = baseColumns.filter((col) => {
          let definition = this.columnDefinitions?.find((def) => columnId(def) === col.id);
          return definition?.isFixed;
        }).length;

        baseColumns = [
          ...baseColumns.slice(0, fixedColumnsCount),
          { id: 'relevantPart', width: 340 },
          ...baseColumns.slice(fixedColumnsCount),
        ];
      }
    } else {
      // Remove relevantPart column when not searching
      baseColumns = baseColumns.filter((col) => col.id !== 'relevantPart');
    }

    return baseColumns;
  }

  private getBaseSelectedColumns() {
    if (!this.isViewInbox) {
      return this.settings.selectedColumns;
    }

    if (!this.session.workspace.isFeatureEnabled('inbox-views-pinned-columns')) {
      return this.settings.selectedColumns;
    }

    let view = this.args.inbox as View;
    let pinnedColumns = view.viewSummary.pinnedColumns || [];
    let userSelectedColumns = this.settings.selectedColumns;

    let columns = this.pinnedColumnsService
      .mergeColumnsWithPinned(
        userSelectedColumns.map((col) => col.id),
        pinnedColumns,
      )
      .map((id) => ({ id, width: userSelectedColumns.find((col) => col.id === id)?.width }));

    if (columns.length !== userSelectedColumns.length) {
      this.saveSettings(columns);
    }

    return columns;
  }

  @task({ drop: true }) *loadDynamicColumns(): TaskGenerator<void> {
    let [descriptors, ticketTypes] = yield Promise.all([
      this.session.workspace.fetchConversationAttributeDescriptors(),
      this.inboxApi.listTicketTypes(),
    ]);

    this.descriptors = descriptors;
    this.ticketTypes = ticketTypes;

    this.updateColumnDefinitions();
  }

  updateColumnDefinitions() {
    this.columnDefinitions = new ColumnDefinitionsBuilder(
      getOwner(this),
      this.ticketTypes,
      this.descriptors,
      this.isSearching ? 'search' : 'inbox',
    ).columnDefinitions;
  }

  @action onPreviewConversation(conversation: ConversationTableEntry) {
    this.router.transitionTo('inbox.workspace.inbox.inbox.conversation.conversation', conversation);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      object: 'conversation',
      section: 'conversation_table',
      conversation_id: conversation.id,
    });
  }

  @action onClosePreview() {
    this.router.transitionTo('inbox.workspace.inbox.inbox.index', {
      queryParams: { skipRedirection: true },
    });
  }

  @action changeColumns(columns: ConversationsTableColumn[]) {
    this.tableColumns!.selectedColumns = columns.map((col) => ({
      id: columnId(col),
      width: col.width,
    }));
    this.saveSettings();
  }

  @action onSortColumn(column: ConversationsTableColumn, direction: SortDirection) {
    if (!column.sortField) {
      return;
    }

    this.inboxState.selectedConversations.clear();
    this.selectedSortOption = { sortField: column.sortField, direction };
    this.saveSettings();
  }

  @action changeStateOption(value: InboxStateOption) {
    this.inboxState.selectedConversations.clear();
    this.inboxState.clearActiveConversation();
    this.selectedStateOption = value;

    this.saveSettings();
  }

  saveSettings(selectedColumns?: SelectedColumn[]) {
    let commonArgs = {
      selectedColumns: selectedColumns || this.tableColumns!.selectedColumns,
      selectedSortOption: this.selectedSortOption,
      selectedStateOption: this.selectedStateOption,
      selectedMentionsStatus: this.selectedMentionsStatus,
    };

    this.conversationsTableSettings.saveInboxSettings(this.args.inbox, { ...commonArgs });
  }

  get fields() {
    if (!this.tableColumns) {
      return [];
    }

    return [
      ...REQUIRED_CONVERSATIONS_TABLE_FIELDS,
      ...(this.args.inbox?.type === InboxType.Mentions ? ['last_admin_mentioned_part'] : []),
      ...(this.session.workspace.hasStandaloneToSuiteMigrationFeature
        ? ['external_reference']
        : []),
      ...this.tableColumns.columns.flatMap((column) => column.fields),
    ].uniq();
  }

  get hiddenColumns() {
    return this.tableColumns?.hiddenColumns ?? [];
  }

  get columns() {
    return this.tableColumns?.columns ?? [];
  }

  @action async onConversationStateChanged(
    conversation: ConversationTableEntry,
    nextNavigableConversation: ConversationTableEntry,
  ) {
    if (conversation.id !== this.inboxState.activeConversationId) {
      return null;
    }

    if (
      !this.isMentionsInbox &&
      !isConversationStateEqual(
        this.selectedStateOption,
        conversation.state!,
        conversation.ticketState,
      )
    ) {
      if (nextNavigableConversation) {
        return await this.router.transitionTo(
          'inbox.workspace.inbox.inbox.conversation.conversation',
          nextNavigableConversation,
        );
      } else {
        return await this.router.transitionTo('inbox.workspace.inbox.inbox.index', {
          queryParams: { skipRedirection: true },
        });
      }
    }

    return null;
  }

  @action async onPinColumn(column: ColumnDefinition) {
    if (!this.isViewInbox) {
      return;
    }

    let view = this.args.inbox as View;
    column.isPinned = true;

    try {
      await this.pinnedColumnsService.updateViewPinnedColumns(view.id, [columnId(column)], []);
    } catch (error) {
      column.isPinned = false;

      this.notificationsService.notifyError(
        this.intl.t('inbox.conversations-table.errors.pin-column'),
      );
    }
  }

  @action async onUnpinColumn(column: ColumnDefinition) {
    if (!this.isViewInbox) {
      return;
    }

    let view = this.args.inbox as View;
    column.isPinned = false;

    try {
      await this.pinnedColumnsService.updateViewPinnedColumns(view.id, [], [columnId(column)]);
    } catch (error) {
      column.isPinned = true;

      this.notificationsService.notifyError(
        this.intl.t('inbox.conversations-table.errors.unpin-column'),
      );
    }
  }

  @tracked private pendingSearchQuery = '';

  @restartableTask *debouncedSearch(query: string): TaskGenerator<void> {
    this.pendingSearchQuery = query;

    // Skip debounce when clearing search - show all results immediately
    if (query.trim().length === 0) {
      this.searchQuery = query;
      this.updateColumnDefinitions();
      return;
    }

    yield timeout(300);
    this.searchQuery = query;
    this.updateColumnDefinitions();
  }

  @action onSearchQueryChange(query: string) {
    taskFor(this.debouncedSearch).perform(query);
  }

  @action onSearchClear() {
    this.pendingSearchQuery = '';
    taskFor(this.debouncedSearch).perform('');
  }

  get displaySearchQuery() {
    // Use pending query for immediate UI feedback
    return this.pendingSearchQuery;
  }

  get isSearching() {
    return this.searchQuery.trim().length > 0;
  }

  get isSearchLoading() {
    return this.isSearching && taskFor(this.debouncedSearch).isRunning;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::InboxConversationsTable': typeof InboxConversationsTable;
  }
}
